import { Navigate, useLocation, useParams } from 'react-router'

import { getCustomerPath } from '~/shell/app/paths'

/**
 * The URL shape Scout served before its routes moved beneath an app segment.
 *
 * Scout's pages used to hang directly off the customer (`/c/:customer/care-gaps/...`); they now hang
 * off the app (`/c/:customer/scout/care-gaps/...`), because in the suite an app's table is mounted
 * under a literal app segment and holds no opinion about where it lives (Atlas ADR-0014). This route
 * keeps the old shape working: a bookmark or a shared link from before the change lands on the same
 * page rather than on a redirect to the app root, so nothing a user saved is lost.
 *
 * Everything after `care-gaps` is carried across verbatim, including the query string and fragment.
 * That is sound precisely because only the *prefix* changed — the old and new tables are the same
 * table beneath `care-gaps`, so a suffix that matched then matches now. It is deliberately a
 * pathname rewrite rather than a per-route redirect: one rule cannot fall out of step with the
 * app's table the way three would.
 *
 * The redirect replaces the history entry, so the old URL does not become a back-button trap. This
 * is a compatibility shim with a life expectancy: it can go once the old shape has stopped appearing
 * in traffic, and it is the whole reason the `scout` segment is written here as a literal. A shell
 * module naming an app is normally the boundary rule this tree exists to prevent; a redirect from
 * one specific app's historical URLs has no app-agnostic form, and giving it one would mean the
 * shell learning each app's former paths. Deleting the shim is what removes the name.
 */
export const LegacyCareGapsRedirect = () => {
  const { customer } = useParams<{ customer: string }>()
  const { hash, pathname, search } = useLocation()

  if (customer === undefined) {
    return null
  }

  // Rebuilt from the location rather than from the splat param, so the suffix keeps whatever
  // encoding it arrived with: `useParams` hands back decoded values, and re-encoding a decoded
  // category id is how a legitimate `%2F` in one turns into a path separator.
  const legacyPrefix = `${getCustomerPath(customer)}/`
  const suffix = pathname.startsWith(legacyPrefix)
    ? pathname.slice(legacyPrefix.length)
    : ''

  return (
    <Navigate
      replace
      to={`${getCustomerPath(customer)}/scout/${suffix}${search}${hash}`}
    />
  )
}
